import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import {
  Search, Filter, ArrowLeft, User, Mail, Calendar,
  CheckCircle, XCircle, Clock, AlertCircle, MessageSquare
} from 'lucide-react';

const UniversityApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const universityId = localStorage.getItem('university_id');

  useEffect(() => {
    if (!universityId) {
      navigate('/university/login');
      return;
    }
    fetchApplications();
  }, [universityId, navigate]);

  useEffect(() => {
    filterApplications();
  }, [searchQuery, statusFilter, applications]);

  const fetchApplications = async () => {
    try {
      const response = await api.get(`/university-hub/applications?university_id=${universityId}`);
      setApplications(response.data);
      setFilteredApps(response.data);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load applications", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    if (searchQuery) {
      filtered = filtered.filter(app =>
        app.student?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.student?.email?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter || app.decision === statusFilter);
    }

    setFilteredApps(filtered);
  };

  const getStatusBadge = (app) => {
    const decision = app.decision;
    const status = app.status;

    if (decision === 'accepted') return <Badge className="bg-green-100 text-green-700">Accepted</Badge>;
    if (decision === 'denied') return <Badge className="bg-red-100 text-red-700">Denied</Badge>;
    if (decision === 'waitlisted') return <Badge className="bg-purple-100 text-purple-700">Waitlisted</Badge>;
    if (decision === 'deferred') return <Badge className="bg-orange-100 text-orange-700">Deferred</Badge>;
    if (status === 'submitted') return <Badge className="bg-blue-100 text-blue-700">Pending Review</Badge>;
    return <Badge className="bg-gray-100 text-gray-700">{status || 'Unknown'}</Badge>;
  };

  const getUnreadMessageCount = (app) => {
    return (app.messages || []).filter(m => m.from === 'student' && !m.read).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/university/dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <h1 className="text-xl font-semibold">Applications</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by student name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Applications</SelectItem>
                  <SelectItem value="submitted">Pending Review</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="denied">Denied</SelectItem>
                  <SelectItem value="waitlisted">Waitlisted</SelectItem>
                  <SelectItem value="deferred">Deferred</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Applications List */}
        <Card>
          <CardHeader>
            <CardTitle>All Applications ({filteredApps.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredApps.length > 0 ? (
              <div className="space-y-4">
                {filteredApps.map((app, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{app.student?.name || 'Unknown Student'}</h3>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-3 h-3" />
                            {app.student?.email || 'No email'}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <Calendar className="w-3 h-3" />
                            Submitted: {app.created_at ? new Date(app.created_at).toLocaleDateString() : 'Unknown'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getUnreadMessageCount(app) > 0 && (
                          <Badge className="bg-blue-500 text-white flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {getUnreadMessageCount(app)} new
                          </Badge>
                        )}
                        {getStatusBadge(app)}
                        <Link to={`/university/applications/${app._id}`}>
                          <Button data-testid={`review-app-${app._id}`}>Review Application</Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No applications found</p>
                {searchQuery && <p className="text-sm">Try adjusting your search criteria</p>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UniversityApplications;
